import { Button } from "@mui/material"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { confirm } from "lib/@dialogs/confirm"
import { invalidate } from "lib/graphql/cache"
import { prevent } from "lib/prevent"
import { infoSnackbar } from "lib/snackbar/info-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import Iconify from "minimals-template/components/Iconify"
import { useState } from "react"
import { deleteJustification, updateAudit } from "lib/@offline/queued-audits"
import { AuditModal } from "routes/regime/components/audit-modal"

export function JustifyChanges() {
    const {
        original,
        tailored,
        hasChanged,
        title,
        changeType,
        draftId,
        isEdit,
        latestReason,
        referenceId,
        setIsDialogVisible,
        taskId = "intro",
        auditId,
        isNewTask,
    } = useBoundContext()
    const changeReason = changeType && changeType === "intro" ? "changeIntroReason" : "changeTaskReason"
    const [audit, setAudit] = useState({ [changeReason]: latestReason })
    let auditPayload = {
        reason: "",
        changeType,
        idForDraft: draftId,
        taskId,
        taskTitle: title,
        status: "changeComment",
    }
    const discardChanges = isEdit ? (
        <Button
            startIcon={<Iconify icon="mdi:bin" />}
            onClick={removeReason}
            data-cy="dialogActionsRemoveButton"
            sx={{ color: "secondary.main" }}
        >
            Remove
        </Button>
    ) : null

    return (
        <Bound target={audit} onChange={setAudit}>
            <AuditModal
                title=""
                label="Add Justification For Change"
                field={changeReason}
                okCaption="Save"
                textFieldProps={{ InputLabelProps: { shrink: true }, defaultValue: latestReason }}
                ok={addReason}
                cancel={prevent(() => setIsDialogVisible(false))}
                okChildren={discardChanges}
            />
        </Bound>
    )

    async function addReason(value) {
        const reason = value[changeReason].trim()
        if (!reason) {
            setIsDialogVisible(false)
            return
        }
        auditPayload.reason = reason
        const includePayload = hasChanged || isNewTask
        if (includePayload && original !== undefined) {
            auditPayload.original = original ?? {}
        }
        if (includePayload && tailored !== undefined) {
            auditPayload.tailored = tailored ?? {}
        }
        auditPayload.event = "tailoring!custom-schedule-comment"
        try {
            await updateAudit({
                auditId,
                type: "customSchedule",
                referenceId,
                shortDescription: "Reason for change",
                longDescription: isNewTask ? "Added new task" : "",
                payload: { ...auditPayload, hideTitleFromLog: true },
            })

            invalidate("getAuditsByReferenceIdAndType")
            !latestReason ? infoSnackbar("Justification added successfully.") : infoSnackbar("Justification updated.")
        } catch (e) {
            console.warn("Audit update failed (offline or network error):", e?.message || e)
            errorSnackbar("Could not save justification. Please try again when online.")
            return
        }
        setIsDialogVisible(false)
    }

    async function removeReason() {
        if (
            await confirm(
                "This action is permanent and can't be undone. The justification will be permanently deleted.",
                "Delete Justification",
                undefined,
                "Delete"
            )
        ) {
            try {
                await deleteJustification(auditId)
                invalidate("getAuditsByReferenceIdAndType")
                infoSnackbar("Justification deleted")
            } catch (e) {
                console.warn("Justification deletion failed (offline or network error):", e?.message || e)
                errorSnackbar("Could not delete justification. Please try again when online.")
            }
            setIsDialogVisible(false)
        }
    }
}
